import { CommunicationService, CommunicationTableData, CommunicationServiceDependencies } from './types';
import { waitForCompanyID } from '@/js/external-js/variableModules/utils/waitForCompanyID.util';

export class CommunicationServiceImpl implements CommunicationService {
  private db: any;
  private firebaseImport: any;
  private FirebaseFieldValue: any;

  constructor({ db, firebaseImport, FirebaseFieldValue }: CommunicationServiceDependencies) {
    this.db = db;
    this.firebaseImport = firebaseImport;
    this.FirebaseFieldValue = FirebaseFieldValue;
  }

  async fetchCommunicationData(companyId: string): Promise<CommunicationTableData> {
    try {
      const docPath = '/compliance/ismsManual/pages/support';
      const docRef = this.db.doc(docPath);
      const supportDoc = await docRef.get();

      if (!supportDoc.exists) {
        throw new Error('Support document not found');
      }

      const supportData = supportDoc.data();
      const communicationData = supportData.communication;

      // Get the ISMS manual document
      const ismsManualDoc = await this.db
        .collection('customers')
        .doc(companyId)
        .collection('product')
        .doc('policyPackages')
        .collection('ISO27001keyDocuments')
        .doc('ismsManual')
        .get();

      if (!ismsManualDoc.exists) {
        throw new Error('ISMS Manual document not found');
      }

      const communicationsTableMap = await ismsManualDoc.data().communicationsTable;
      const frequencyDropDownArray = communicationsTableMap.frequencyDropDown || [];
      const communicationsChannelArray = communicationsTableMap.communicationChannelDropDown || [];
      const storedValues = communicationsTableMap.storedValues || {};

      const res = deepReplaceISMSWithMSG({
        tableHeaders: communicationData.tableHeaders || [],
        tableData: communicationData.tableData || [],
        frequencyDropDown: frequencyDropDownArray,
        communicationChannelDropDown: communicationsChannelArray,
        storedValues: storedValues
      });

      return res;
    } catch (error) {
      console.error('Error fetching communication data:', error);
      throw error;
    }
  }

  async updateCommunicationData(
    companyId: string,
    data: Partial<CommunicationTableData>
  ): Promise<void> {
    try {
      const updateData: any = {};
      
      if (data.frequencyDropDown) {
        updateData['communicationsTable.frequencyDropDown'] = data.frequencyDropDown;
      }
      
      if (data.communicationChannelDropDown) {
        updateData['communicationsTable.communicationChannelDropDown'] = data.communicationChannelDropDown;
      }
      
      if (data.storedValues) {
        for (const [key, value] of Object.entries(data.storedValues)) {
          updateData[`communicationsTable.storedValues.${key}`] = value;
        }
      }

      await this.db
        .collection('customers')
        .doc(companyId)
        .collection('product')
        .doc('policyPackages')
        .collection('ISO27001keyDocuments')
        .doc('ismsManual')
        .update(updateData);
    } catch (error) {
      console.error('Error updating communication data:', error);
      throw error;
    }
  }

  async addNewFrequency(frequency: string): Promise<void> {
    try {
      const companyId = await waitForCompanyID();
      const docRef = this.db
        .collection('customers')
        .doc(companyId)
        .collection('product')
        .doc('policyPackages')
        .collection('ISO27001keyDocuments')
        .doc('ismsManual');

      await docRef.update({
        'communicationsTable.frequencyDropDown': this.FirebaseFieldValue.arrayUnion(frequency)
      });
    } catch (error) {
      console.error('Error adding new frequency:', error);
      throw error;
    }
  }

  async addNewCommunicationChannel(channel: string): Promise<void> {
    try {
      const companyId = await waitForCompanyID();
      const docRef = this.db
        .collection('customers')
        .doc(companyId)
        .collection('product')
        .doc('policyPackages')
        .collection('ISO27001keyDocuments')
        .doc('ismsManual');

      await docRef.update({
        'communicationsTable.communicationChannelDropDown': this.FirebaseFieldValue.arrayUnion(channel)
      });
    } catch (error) {
      console.error('Error adding new communication channel:', error);
      throw error;
    }
  }
}

// Factory function to create the service
export const createCommunicationService = (deps: CommunicationServiceDependencies): CommunicationService => {
  return new CommunicationServiceImpl(deps);
};

// Default export with Firebase dependencies (to be initialized with actual Firebase instance)
let communicationServiceInstance: CommunicationService | null = null;

export const getCommunicationService = (): CommunicationService => {
  if (!communicationServiceInstance) {
    throw new Error('CommunicationService has not been initialized. Call initCommunicationService first.');
  }
  return communicationServiceInstance;
};

export const initCommunicationService = (deps: CommunicationServiceDependencies): void => {
  if (!communicationServiceInstance) {
    communicationServiceInstance = createCommunicationService(deps);
  }
};


function deepReplaceISMSWithMSG(input: any): any {
  if (typeof input === 'string') {
    return input.replace(/ISMS/g, 'MSG');
  } else if (Array.isArray(input)) {
    return input.map(deepReplaceISMSWithMSG);
  } else if (input && typeof input === 'object') {
    const output: any = {};
    for (const key in input) {
      output[key] = deepReplaceISMSWithMSG(input[key]);
    }
    return output;
  }
  return input;
}